import { db } from '../lib/firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  query, 
  orderBy, 
  limit, 
  where 
} from 'firebase/firestore';

// Simple, reliable Firebase operations with built-in error handling
export class FirebaseSimple {
  private static cache = new Map<string, { data: any; timestamp: number }>();
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Get cached data if available and fresh
  private static getCached<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  // Set cache
  private static setCache(key: string, data: any): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  // Get modules with caching
  static async getModules(): Promise<any[]> {
    const cacheKey = 'modules';
    const cached = this.getCached<any[]>(cacheKey);
    if (cached) return cached;

    try {
      const q = query(
        collection(db, 'modules'),
        orderBy('order', 'asc'),
        limit(20)
      );
      
      const snapshot = await getDocs(q);
      const modules = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        publishedAt: doc.data().publishedAt?.toDate(),
      })).filter((module: any) => module.published);

      this.setCache(cacheKey, modules);
      return modules;
    } catch (error) {
      console.warn('Failed to fetch modules:', error);
      return this.getCached<any[]>(cacheKey) || [];
    }
  }

  // Get user journals with caching
  static async getUserJournals(userId: string): Promise<any[]> {
    const cacheKey = `journals_${userId}`;
    const cached = this.getCached<any[]>(cacheKey);
    if (cached) return cached;

    try {
      const q = query(
        collection(db, 'users', userId, 'journals'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      
      const snapshot = await getDocs(q);
      const journals = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      }));

      this.setCache(cacheKey, journals);
      return journals;
    } catch (error) {
      console.warn('Failed to fetch journals:', error);
      return this.getCached<any[]>(cacheKey) || [];
    }
  }

  // Simple add document
  static async addDocument(collectionPath: string, data: any): Promise<string | null> {
    try {
      const docRef = await addDoc(collection(db, collectionPath), {
        ...data,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      // Clear related cache
      this.clearCacheByPattern(collectionPath);
      return docRef.id;
    } catch (error) {
      console.warn('Failed to add document:', error);
      return null;
    }
  }

  // Simple update document
  static async updateDocument(docPath: string, data: any): Promise<boolean> {
    try {
      await updateDoc(doc(db, docPath), {
        ...data,
        updatedAt: new Date()
      });
      
      // Clear related cache
      this.clearCacheByPattern(docPath.split('/')[0]);
      return true;
    } catch (error) {
      console.warn('Failed to update document:', error);
      return false;
    }
  }

  // Get single document
  static async getDocument(docPath: string): Promise<any | null> {
    const cacheKey = `doc_${docPath}`;
    const cached = this.getCached<any>(cacheKey);
    if (cached) return cached;

    try {
      const docSnap = await getDoc(doc(db, docPath));
      if (docSnap.exists()) {
        const data = {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate(),
          updatedAt: docSnap.data().updatedAt?.toDate(),
        };
        
        this.setCache(cacheKey, data);
        return data;
      }
      return null;
    } catch (error) {
      console.warn('Failed to get document:', error);
      return this.getCached<any>(cacheKey) || null;
    }
  }

  // Clear cache by pattern
  private static clearCacheByPattern(pattern: string): void {
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  // Clear all cache
  static clearCache(): void {
    this.cache.clear();
  }

  // Check if Firebase is working
  static async isHealthy(): Promise<boolean> {
    try {
      // Try a simple operation with short timeout
      const testPromise = getDocs(query(collection(db, 'users'), limit(1)));
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 2000)
      );
      
      await Promise.race([testPromise, timeoutPromise]);
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Simple hooks for components
import React from 'react';

export const useFirebaseData = <T>(
  operation: () => Promise<T>,
  fallback: T,
  dependencies: any[] = []
) => {
  const [data, setData] = React.useState<T>(fallback);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const result = await operation();
        if (mounted) {
          setData(result);
        }
      } catch (err: any) {
        if (mounted) {
          setError(err.message || 'Unknown error');
          console.warn('Firebase operation failed:', err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, dependencies);

  return { data, loading, error };
};
